import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, formatApiError } from '@/lib/AuthContext';
import { Eye, EyeOff, Lock, Mail, AlertTriangle, Shield } from 'lucide-react';
import { trackEvent } from '@/components/SEOHead';

export default function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return');
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      trackEvent('login');
      if (user.must_change_password) {
        navigate('/change-password');
      } else if (returnUrl) {
        navigate(decodeURIComponent(returnUrl));
      } else {
        navigate(user.is_platform_admin ? '/admin' : '/dashboard');
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2533] flex items-center justify-center px-4" data-testid="signin-page">
      <main id="main-content" className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-white font-bold text-2xl">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1]">|</span>
            <span className="text-[#4DD0E1] font-semibold text-2xl">TradePath</span>
          </div>
          <p className="text-[#B0BEC5] text-sm">Supply Chain Intelligence Platform</p>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="h-5 w-5 text-[#0097A7]" />
            <h1 className="text-xl font-bold text-[#0A2533]">Sign in</h1>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-lg p-3 mb-4" role="alert" data-testid="signin-error">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="signin-email" className="block text-sm font-medium text-[#4E6974] mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" aria-hidden="true" />
                <input
                  id="signin-email"
                  data-testid="signin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] placeholder:text-[#B0BEC5] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none transition-colors"
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="signin-password" className="block text-sm font-medium text-[#4E6974] mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" aria-hidden="true" />
                <input
                  id="signin-password"
                  data-testid="signin-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] placeholder:text-[#B0BEC5] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none transition-colors"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B0BEC5] hover:text-[#4E6974]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="signin-toggle-password">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              data-testid="signin-submit"
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white font-medium text-sm rounded-lg transition-colors duration-200"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-sm text-[#4E6974] mt-5">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#0097A7] hover:underline font-medium" data-testid="signin-register-link">Create one</Link>
          </p>
        </div>

        <p className="text-center text-[#4E6974] text-xs mt-6">
          Protected by Nuvanta security protocols
        </p>
      </main>
    </div>
  );
}
